import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  TextField,
  Stack,
  Card,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  TablePagination,
  Radio,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { UserFilterPagination, theme } from './TableStyles';
import { DefaultOutlinedButton, DefaultContainedButton } from './Buttons';
import { CustomAutocomplete, CustomTextField, CustomDatePicker } from './StyledInputs';
import { HttpErrorHandler } from './ErrorHandler';
import { LoadingUI } from './LayoutStyles';
import API from '../api';
import { usePopup } from '../context/PopupContext';

dayjs.locale('it');

export const UserFilterDialog = ({ openDialog, closeDialog, outputUser }) => {
  const { showPopup, setPopUpText } = usePopup();

  const [userName, setUserName] = useState('');
  const [userSurname, setUserSurname] = useState('');
  const [userAlias, setUserAlias] = useState('');
  const [userBirthDateFrom, setUserBirthDateFrom] = useState('');
  const [userBirthDateTo, setUserBirthDateTo] = useState('');
  const [userFiscalCode, setUserFiscalCode] = useState('');

  const [countriesList, setCountriesList] = useState([]);
  const [provincesList, setProvincesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [apiCalled, setApiCalled] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const [userList, setUserList] = useState([]);
  const [selectedUser, setSelectedUser] = useState({});

  const handleSelectUser = (user) => {
    if (Object.keys(selectedUser).length > 0) {
      if (user.id === selectedUser.id) {
        setSelectedUser({});
      } else {
        setSelectedUser(user);
      }
    } else {
      setSelectedUser(user);
    }
  };

  // Select Birth Date
  const handleBirthDateFromChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setUserBirthDateFrom(formattedDate);
  };

  // Select Birth Date
  const handleBirthDateToChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setUserBirthDateTo(formattedDate);
  };

  // Get Countries and Entity Types
  useEffect(() => {
    API.get(`/countries`).then((response) => {
      setCountriesList(response.data);
    });
  }, []);

  // Set Loading Status
  useEffect(() => {
    if (countriesList.length > 0) {
      setApiCalled(true);
    }
  }, [countriesList]);

  //   Get Italy Default Country
  useEffect(() => {
    const italy = countriesList.find((element) => element.name === 'Italia');
    setSelectedCountry(italy);
  }, [countriesList]);

  // //   Get Regions List
  // useEffect(() => {
  //   if (selectedCountry !== null && selectedCountry !== undefined && selectedCountry !== '') {
  //     if (typeof selectedCountry === 'object' && Object.keys(selectedCountry).length > 0) {
  //       API.get(`/countries/regions`, {
  //          params: {
  //            id: selectedCountry.id}
  // }
  //          ).then((response) => {
  //         if (response.data.length > 0) {
  //           setRegionsList(response.data);
  //         } else {
  //           setRegionsList([]);
  //           setProvincesList([]);
  //           setCitiesList([]);
  //         }
  //       });
  //     }
  //   }
  // }, [selectedCountry]);

  //   Get Provinces List
  useEffect(() => {
    if (selectedCountry !== null && selectedCountry !== undefined && selectedCountry !== '') {
      if (typeof selectedCountry === 'object' && Object.keys(selectedCountry).length > 0) {
        API.get(`/provinces`).then((response) => {
          if (response.data.length > 0) {
            setProvincesList(response.data);
          } else {
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedCountry]);

  //   Get Cities List
  useEffect(() => {
    if (selectedProvince !== null && selectedProvince !== undefined && selectedProvince !== '') {
      if (typeof selectedProvince === 'object' && Object.keys(selectedProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: selectedProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setCitiesList(response.data);
          } else {
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedProvince]);

  const emptyField = () => {
    closeDialog();
    setUserName('');
    setUserSurname('');
    setUserAlias('');
    setUserBirthDateFrom('');
    setUserBirthDateTo('');
    setUserFiscalCode('');
    setSelectedProvince(null);
    setSelectedCity(null);
    setSelectedUser({});
    setProvincesList([]);
    setCitiesList([]);
    setUserList([]);
  };

  const [apiRequest, setApiRequest] = useState('');
  const [apiResponseData, setApiResponseData] = useState({});

  const getFilteredUsers = (e) => {
    e.preventDefault();

    const apiCall = `/users/search?${userName.length > 0 ? `name=${userName}` : ''}${
      userSurname.length > 0 ? `&surname=${userSurname}` : ''
    }${
      userBirthDateFrom.length > 0 && userBirthDateFrom !== 'Invalid Date'
        ? `&date_of_birth_from=${dayjs(userBirthDateFrom).format('DD-MM-YYYY')}`
        : ''
    }${
      userBirthDateTo.length > 0 && userBirthDateTo !== 'Invalid Date'
        ? `&date_of_birth_to=${dayjs(userBirthDateTo).format('DD-MM-YYYY')}`
        : ''
    }${userFiscalCode.length > 0 ? `&fiscal_code=${userFiscalCode}` : ''}${
      userAlias.length > 0 ? `&alias=${userAlias}` : ''
    }${selectedCountry ? `&country=${selectedCountry.id}` : ''}${
      selectedProvince ? `&province=${selectedProvince.id}` : ''
    }${selectedCity ? `&city=${selectedCity.id}` : ''}`;

    setApiRequest(apiCall);

    API.get(apiCall)
      .then((response) => {
        setApiResponseData(response.data);
        showPopup(true, 'Utenti filtrati con successo');
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const handleSubmitFilter = () => {
    if (Object.keys(selectedUser).length > 0) {
      outputUser(selectedUser);
      emptyField();
    } else {
      showPopup(false, 'Selezionare un utente');
    }
  };

  useEffect(() => {
    if (Object.keys(apiResponseData).length > 0) {
      setUserList(apiResponseData.data);
    }
  }, [apiResponseData]);

  /* ------------------------------- Pagination ------------------------------- */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sliceStartDisplay, setSliceStartDisplay] = useState(0);
  const [sliceEndDisplay, setSliceEndDisplay] = useState(0);

  useEffect(() => {
    if (page % 2 === 0) {
      setSliceStartDisplay(0);
      setSliceEndDisplay(10);
    } else {
      setSliceStartDisplay(10);
      setSliceEndDisplay(20);
    }
  }, [page]);

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog open={openDialog} fullWidth maxWidth={'lg'} onClose={() => {}}>
      <DialogTitle sx={{ textAlign: 'center' }}>Filtra Utenti</DialogTitle>
      <DialogContent>
        {apiCalled ? (
          <>
            <Grid container spacing={3} sx={{ marginTop: '4px' }}>
              {/* User Name */}
              <Grid item xs={12} md={3}>
                <CustomTextField label="Nome" value={userName} onChange={(e) => setUserName(e.currentTarget.value)} />
              </Grid>

              {/* User Surname */}
              <Grid item xs={12} md={3}>
                <CustomTextField
                  label="Cognome"
                  value={userSurname}
                  onChange={(e) => setUserSurname(e.currentTarget.value)}
                />
              </Grid>

              {/* User Alias */}
              <Grid item xs={12} md={3}>
                <CustomTextField
                  label="Alias"
                  value={userAlias}
                  onChange={(e) => setUserAlias(e.currentTarget.value)}
                />
              </Grid>

              {/* User Birth Date From */}
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                  <CustomDatePicker
                    label="Data di Nascita da"
                    onChange={handleBirthDateFromChange}
                    format="DD/MM/YYYY"
                  />
                </LocalizationProvider>
              </Grid>

              {/* User Birth Date To */}
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                  <CustomDatePicker label="Data di Nascita a" onChange={handleBirthDateToChange} format="DD/MM/YYYY" />
                </LocalizationProvider>
              </Grid>

              {/* User Fiscal Code */}
              <Grid item xs={12} md={3}>
                <CustomTextField
                  label="Codice Fiscale"
                  value={userFiscalCode}
                  onChange={(e) => setUserFiscalCode(e.currentTarget.value)}
                />
              </Grid>

              {/* Country */}
              <Grid item xs={12} md={3}>
                <CustomAutocomplete
                  options={countriesList.filter(
                    (country) => !selectedProvince || country.id === selectedProvince.country_id
                  )}
                  getOptionLabel={(option) => option.name}
                  defaultValue={selectedCountry}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  onChange={(e, value) => setSelectedCountry(value)}
                  renderInput={(params) => <TextField {...params} label="Paese" variant="outlined" />}
                />
              </Grid>

              {/* Region */}
              {/* <Grid item xs={12} md={3}>
                <CustomAutocomplete
                  options={regionsList.filter(
                    (region) =>
                      (!selectedProvince || region.region_name === selectedProvince.region_name) &&
                      (!selectedCountry || region.country_id === selectedCountry.id)
                  )}
                  isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                  getOptionLabel={(option) => option.region_name}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.region_name}>
                      {option.region_name}
                    </Box>
                  )}
                  onChange={(e, value) => setSelectedRegion(value)}
                  renderInput={(params) => <TextField {...params} label="Regione" variant="outlined" />}
                />
              </Grid> */}

              {/* Province */}
              <Grid item xs={12} md={3}>
                <CustomAutocomplete
                  options={provincesList.filter(
                    (province) =>
                      (!selectedCity || province.id === selectedCity.province_id) &&
                      (!selectedCountry || province.country_id === selectedCountry.id)
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  onChange={(e, value) => setSelectedProvince(value)}
                  renderInput={(params) => <TextField {...params} label="Provincia" variant="outlined" />}
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} md={3}>
                <CustomAutocomplete
                  options={citiesList.filter((city) => !selectedProvince || city.province_id === selectedProvince.id)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  onChange={(e, value) => setSelectedCity(value)}
                  renderInput={(params) => <TextField {...params} label="Comune" variant="outlined" />}
                />
              </Grid>

              <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <Stack direction="row" justifyContent="center" sx={{ width: '100%' }}>
                  <DefaultOutlinedButton customStyles={{ width: '100%' }} onClick={getFilteredUsers}>
                    Cerca Utente
                  </DefaultOutlinedButton>
                </Stack>
              </Grid>
            </Grid>

            <Card sx={{ marginTop: '24px' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Nome e Cognome</TableCell>
                      <TableCell>Codice Fiscale</TableCell>
                      <TableCell>Data di Nascita</TableCell>
                      {/* <TableCell>Entità Master</TableCell>
                      <TableCell>Residenza</TableCell> */}
                    </TableRow>
                  </TableHead>

                  {userList.length > 0 ? (
                    <TableBody>
                      {userList.slice(sliceStartDisplay, sliceEndDisplay).map((user) => (
                        <TableRow
                          sx={{
                            backgroundColor: user.id === selectedUser.id ? '#d0f8dc' : 'inherit',
                            '&:hover': { backgroundColor: '#c3f8d3' },
                          }}
                          hover={user.id !== selectedUser.id}
                          aria-checked={user.id === selectedUser.id}
                          key={user.id}
                          onClick={() => handleSelectUser(user)}
                        >
                          <TableCell>
                            <Radio checked={user.id === selectedUser.id} />
                          </TableCell>
                          <TableCell>
                            {user.name} {user.surname}
                          </TableCell>
                          <TableCell>{user.fiscal_code}</TableCell>
                          <TableCell>{dayjs(user.birth_date).format('DD/MM/YYYY')}</TableCell>
                          {/* <TableCell>{user.entity_id}</TableCell>
                          <TableCell>comune</TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                          Nessun utente disponibile
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}

                  <TableFooter>
                    <TableRow>
                      <ThemeProvider theme={theme}>
                        <TablePagination
                          sx={{ borderBottom: 'none' }}
                          rowsPerPageOptions={[]}
                          count={Object.keys(apiResponseData).length > 0 ? apiResponseData.total : 0}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={(props) => (
                            <UserFilterPagination
                              {...props}
                              apiResponseData={apiResponseData}
                              setApiResponseData={setApiResponseData}
                              lastPage={apiResponseData.last_page}
                              apiRequest={apiRequest}
                            />
                          )}
                        />
                      </ThemeProvider>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Card>
          </>
        ) : (
          LoadingUI()
        )}
      </DialogContent>
      <DialogActions>
        <DefaultContainedButton
          onClick={() => {
            handleSubmitFilter();
          }}
        >
          Seleziona Utente
        </DefaultContainedButton>
        <DefaultOutlinedButton onClick={() => emptyField()}>Chiudi</DefaultOutlinedButton>

      </DialogActions>
    </Dialog>
  );
};
